/**
 * StatusPage.tsx — acompanhamento de geração assíncrona.
 *
 * Substitui as 9 páginas *_status.html (macro status_premium_macros.html):
 * UM componente, 5 estados exaustivos (o switch sem default é checado pelo
 * TypeScript) e a identidade visual de cada domínio vinda da configuração.
 *
 * Réplica do layout original: header com gradiente, "Licitação: <objeto>",
 * alerta de status, barra de progresso com o % dentro, detalhe da etapa,
 * timeline das fases, aviso de navegação livre e os botões de ação.
 */

import {
  faArrowLeft,
  faCheckCircle,
  faCircle,
  faCompass,
  faExclamationTriangle,
  faEye,
  faFileContract,
  faHandPointer,
  faInfoCircle,
  faSpinner,
  faSyncAlt,
  faTasks,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useEffect } from 'react'
import { Navigate, useLocation, useParams } from 'react-router'
import { ApiError } from '../api/http'
import type { StatusTask, TipoTask } from '../api/tasks'
import {
  CONFIGURACAO,
  estadoDaFase,
  type ConfiguracaoTipo,
} from '../features/tasks/configuracao'
import { useRastreador } from '../features/tasks/rastreador'
import { useTaskStatus } from '../hooks/useTaskStatus'

const TIPOS_VALIDOS = new Set(Object.keys(CONFIGURACAO))

/** Segundos até o redirect automático quando conclui (regra do original). */
const ESPERA_REDIRECT_MS = 2000

function Alerta({ status }: { status: StatusTask }) {
  const estilos = {
    erro: 'border-red-400 bg-red-50 text-red-800 dark:border-red-600 dark:bg-red-900/20 dark:text-red-300',
    ok: 'border-green-400 bg-green-50 text-green-800 dark:border-green-600 dark:bg-green-900/20 dark:text-green-300',
    aviso: 'border-amber-400 bg-amber-50 text-amber-800 dark:border-amber-600 dark:bg-amber-900/20 dark:text-amber-300',
    andamento: 'border-blue-400 bg-blue-50 text-blue-800 dark:border-blue-600 dark:bg-blue-900/20 dark:text-blue-300',
  }
  const { classe, icone, texto, girando } =
    status.estado === 'FALHOU'
      ? { classe: estilos.erro, icone: faExclamationTriangle, texto: 'Erro na geração', girando: false }
      : status.estado === 'CONCLUIDO'
        ? { classe: estilos.ok, icone: faCheckCircle, texto: 'Documento gerado com sucesso!', girando: false }
        : status.estado === 'AGUARDANDO_SELECAO'
          ? { classe: estilos.aviso, icone: faHandPointer, texto: 'Ação necessária para continuar', girando: false }
          : { classe: estilos.andamento, icone: faSpinner, texto: 'O documento está sendo gerado...', girando: true }

  return (
    <div className={`mb-6 flex items-center gap-3 rounded-r-xl border-l-4 p-4 ${classe}`}>
      <FontAwesomeIcon icon={icone} spin={girando} className="text-xl" />
      <span className="font-medium">{texto}</span>
    </div>
  )
}

function BarraProgresso({
  percentual,
  config,
  concluido,
}: {
  percentual: number
  config: ConfiguracaoTipo
  concluido: boolean
}) {
  return (
    <div className="mb-6">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
          Progresso
        </span>
        <span className={`text-sm font-bold ${config.acento}`}>{percentual}%</span>
      </div>
      <div className="h-6 w-full overflow-hidden rounded-full bg-slate-200 shadow-inner dark:bg-slate-700">
        <div
          className={`flex h-6 items-center justify-center rounded-full bg-gradient-to-r transition-all duration-500 ease-out ${
            concluido ? 'from-emerald-500 to-green-500' : config.barra
          }`}
          style={{ width: `${percentual}%` }}
        >
          <span
            className={`text-xs font-bold text-white transition-opacity ${
              percentual > 20 ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {percentual}%
          </span>
        </div>
      </div>
    </div>
  )
}

function Fases({
  config,
  percentual,
}: {
  config: ConfiguracaoTipo
  percentual: number
}) {
  return (
    <div className="mb-6">
      <h3 className="mb-4 flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-300">
        <FontAwesomeIcon icon={faTasks} className={config.acento} />
        Etapas do Processamento:
      </h3>
      <ol className="space-y-3">
        {config.fases.map((fase, indice) => {
          const estado = estadoDaFase(indice, config.fases.length, percentual)
          const circulo =
            estado === 'concluida'
              ? 'bg-green-100 dark:bg-green-900/30'
              : estado === 'andamento'
                ? 'bg-blue-100 dark:bg-blue-900/30'
                : 'bg-slate-300 dark:bg-slate-600'
          const texto =
            estado === 'concluida'
              ? 'font-medium text-green-700 dark:text-green-400'
              : estado === 'andamento'
                ? 'font-medium text-blue-700 dark:text-blue-400'
                : 'text-slate-600 dark:text-slate-400'
          return (
            <li
              key={fase}
              className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-600 dark:bg-slate-700/50"
            >
              <span className={`flex h-8 w-8 items-center justify-center rounded-full ${circulo}`}>
                {estado === 'concluida' && (
                  <FontAwesomeIcon icon={faCheckCircle} className="text-sm text-green-500" />
                )}
                {estado === 'andamento' && (
                  <FontAwesomeIcon icon={faSpinner} spin className="text-sm text-blue-500" />
                )}
                {estado === 'pendente' && (
                  <FontAwesomeIcon icon={faCircle} className="text-xs text-slate-400 dark:text-slate-500" />
                )}
              </span>
              <span className={texto}>
                {indice + 1}. {fase}
              </span>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

function ConteudoStatus({
  status,
  aoAtualizar,
}: {
  status: StatusTask
  aoAtualizar: () => void
}) {
  const config = CONFIGURACAO[status.recurso.tipo]
  const concluido = status.estado === 'CONCLUIDO'
  const percentual = concluido ? 100 : status.progresso.percentual
  const emAndamento = status.estado === 'PENDENTE' || status.estado === 'EXECUTANDO'

  // Redirect automático 2s após concluir (comportamento do template legado)
  useEffect(() => {
    if (!concluido || !status.resultado?.view_url) return undefined
    const url = status.resultado.view_url
    const t = setTimeout(() => {
      window.location.href = url // pode ser página Jinja: full page load
    }, ESPERA_REDIRECT_MS)
    return () => clearTimeout(t)
  }, [concluido, status.resultado])

  return (
    <div className="mx-auto max-w-4xl">
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <div className={`bg-gradient-to-r p-6 text-white ${config.cabecalho}`}>
          <div className="flex items-center gap-4">
            <span className="flex h-14 w-14 items-center justify-center rounded-xl bg-white/20 shadow-lg backdrop-blur-sm">
              <FontAwesomeIcon icon={config.icone} className="text-2xl" />
            </span>
            <div>
              <h1 className="mb-1 text-2xl font-bold">{config.titulo}</h1>
              <p className={config.subtituloCor}>{config.subtitulo}</p>
            </div>
          </div>
        </div>

        <div className="p-6 md:p-8">
          {status.titulo && (
            <h2 className="mb-6 flex flex-wrap items-center gap-2 text-xl font-semibold text-slate-800 dark:text-slate-100">
              <FontAwesomeIcon icon={faFileContract} className={config.acento} />
              <span>Licitação:</span>
              <span className="text-base font-normal text-slate-600 dark:text-slate-400">
                {status.titulo}
              </span>
            </h2>
          )}

          <Alerta status={status} />

          {status.estado === 'FALHOU' && (
            <div className="mb-6 rounded-r-xl border-l-4 border-red-400 bg-red-50 p-4 dark:border-red-600 dark:bg-red-900/20">
              <div className="flex items-start gap-3">
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  className="mt-0.5 text-xl text-red-600 dark:text-red-400"
                />
                <div>
                  <strong className="font-medium text-red-800 dark:text-red-300">
                    Erro no processamento:
                  </strong>
                  <div className="mt-1 text-red-700 dark:text-red-400">
                    {status.erro?.mensagem ?? 'Erro desconhecido.'}
                  </div>
                </div>
              </div>
            </div>
          )}

          <BarraProgresso percentual={percentual} config={config} concluido={concluido} />

          <div className="mb-6 text-center">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-600 dark:bg-slate-700/50">
              <FontAwesomeIcon
                icon={concluido ? faCheckCircle : faInfoCircle}
                className={`mr-2 ${concluido ? 'text-green-600' : config.acento}`}
              />
              <span className="text-lg font-medium text-slate-700 dark:text-slate-300">
                {concluido
                  ? `${config.rotulo} concluído!`
                  : status.progresso.mensagem || config.mensagemInicial}
              </span>
            </div>
          </div>

          <Fases config={config} percentual={percentual} />

          {emAndamento && (
            <div className="mb-6 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-800 dark:bg-emerald-900/20">
              <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-green-600">
                <FontAwesomeIcon icon={faCompass} className="text-white" />
              </span>
              <div>
                <p className="font-semibold text-emerald-800 dark:text-emerald-300">
                  Você pode navegar livremente!
                </p>
                <p className="mt-1 text-sm text-emerald-600 dark:text-emerald-400">
                  O processamento continuará em segundo plano. Você será
                  notificado quando concluir.
                </p>
              </div>
            </div>
          )}

          <div className="flex flex-wrap justify-center gap-3">
            {concluido && status.resultado && (
              <a
                href={status.resultado.view_url}
                className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-emerald-500 to-green-600 px-6 py-3 font-semibold text-white shadow-lg shadow-emerald-500/30 transition-all hover:scale-105 hover:from-emerald-600 hover:to-green-700"
              >
                <FontAwesomeIcon icon={faEye} />
                {config.rotuloVisualizar}
              </a>
            )}
            {status.estado === 'AGUARDANDO_SELECAO' && status.acao_pendente && (
              <a
                href={status.acao_pendente.url}
                className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 px-6 py-3 font-semibold text-white shadow-lg shadow-amber-500/30 transition-all hover:scale-105"
              >
                <FontAwesomeIcon icon={faHandPointer} />
                {status.acao_pendente.tipo === 'selecao_precos'
                  ? 'Selecionar Preços'
                  : 'Selecionar Documentos'}
              </a>
            )}
            {!concluido && (
              <button
                type="button"
                onClick={aoAtualizar}
                className={`flex items-center gap-2 rounded-xl bg-gradient-to-r px-6 py-3 font-semibold text-white shadow-lg transition-all hover:scale-105 hover:opacity-90 ${config.botao}`}
              >
                <FontAwesomeIcon icon={faSyncAlt} />
                Atualizar Status
              </button>
            )}
            <a
              href="/app/dashboard"
              className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-slate-500 to-slate-600 px-6 py-3 font-semibold text-white shadow-lg transition-all hover:from-slate-600 hover:to-slate-700"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              Voltar
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

export function StatusPage() {
  const { tipo, id } = useParams()
  const localizacao = useLocation()
  const { registrar } = useRastreador()
  const idNumerico = Number(id)
  const tipoValido = tipo !== undefined && TIPOS_VALIDOS.has(tipo)
  const idValido = Number.isFinite(idNumerico) && idNumerico > 0

  const consulta = useTaskStatus(
    (tipoValido ? tipo : 'dfd') as TipoTask,
    idValido ? idNumerico : 0,
  )

  // Abrir a página de status registra a tarefa no painel flutuante — é o
  // que o GlobalTaskTracker.register() fazia no onload de cada template.
  const estado = consulta.data?.estado
  useEffect(() => {
    if (!tipoValido || !idValido) return
    if (!estado || estado === 'CONCLUIDO' || estado === 'FALHOU') return
    registrar(tipo as TipoTask, idNumerico)
  }, [tipoValido, idValido, estado, tipo, idNumerico, registrar])

  if (!tipoValido || !idValido) return <Navigate to="/" replace />

  if (consulta.error instanceof ApiError && consulta.error.codigo === 'NAO_AUTENTICADO') {
    return <Navigate to={`/login?next=${encodeURIComponent(localizacao.pathname)}`} replace />
  }
  if (consulta.error) {
    return (
      <div className="mx-auto mt-12 max-w-xl rounded-2xl bg-white p-8 shadow-lg dark:bg-dark-card">
        <h1 className="mb-2 text-xl font-bold text-red-700 dark:text-red-400">
          Não foi possível carregar o status
        </h1>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {consulta.error.message}
        </p>
      </div>
    )
  }
  if (!consulta.data) {
    return (
      <div className="mt-12 text-center text-slate-500">
        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
        Carregando status…
      </div>
    )
  }
  return (
    <ConteudoStatus status={consulta.data} aoAtualizar={() => void consulta.refetch()} />
  )
}
